import React from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { motion, type Variants } from 'framer-motion'
import { ArrowLeft, ShieldCheck } from '@phosphor-icons/react'
import { useAuthStore } from '@/stores/authStore'
import { authApi } from '@/lib/api'
import { OtpInput } from '@/components/ui/OtpInput'

const easeOutExpo = [0.16, 1, 0.3, 1] as const

const formStageVariants: Variants = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { staggerChildren: 0.06, delayChildren: 0.1 } },
}

const formItemVariants: Variants = {
  hidden: { opacity: 0, y: 10 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: easeOutExpo } },
}

const shakeVariants: Variants = {
  idle: { x: 0 },
  error: { x: [0, -6, 6, -4, 4, 0], transition: { duration: 0.4, ease: 'easeInOut' } },
}

const DotLoader: React.FC = () => (
  <span className="inline-flex items-center gap-1" aria-hidden="true">
    {[0, 1, 2].map((i) => (
      <motion.span
        key={i}
        className="block h-1 w-1 rounded-full bg-current"
        animate={{ opacity: [0.25, 1, 0.25], y: [0, -2, 0] }}
        transition={{ duration: 0.9, repeat: Infinity, ease: 'easeInOut', delay: i * 0.15 }}
      />
    ))}
  </span>
)

const OTP_LENGTH = 6

const formatMMSS = (totalSeconds: number) => {
  const m = Math.floor(totalSeconds / 60)
  const s = totalSeconds % 60
  return `${m}:${s.toString().padStart(2, '0')}`
}

/** Whole seconds from `now` until `deadline` (both epoch ms), floored at 0. */
export const secondsRemaining = (deadline: number, now: number) =>
  Math.max(Math.ceil((deadline - now) / 1000), 0)

// The backend owns the code's expiry and resend cooldown (GET /auth/otp-status).
// We turn its relative seconds into local deadlines once and count down from
// there, so a refresh or a new tab shows the real time and the user's clock
// being off doesn't matter. 'unavailable' = the status call failed: hide the
// expiry line and leave Resend enabled — the backend still enforces both.
type OtpTimers = { expiresAt: number; resendAt: number } | 'loading' | 'unavailable'

const VerifyOtpPage: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { verifyOtp, resendOtp, isLoading } = useAuthStore()
  const navState = location.state as
    | { email?: string; full_name?: string; username?: string }
    | null
  const email = navState?.email

  const [timers, setTimers] = React.useState<OtpTimers>('loading')
  const [now, setNow] = React.useState(() => Date.now())
  const [code, setCode] = React.useState('')
  const [error, setError] = React.useState<string | null>(null)
  const [isResending, setIsResending] = React.useState(false)
  const timersKnown = typeof timers === 'object'
  const expirySeconds = timersKnown ? secondsRemaining(timers.expiresAt, now) : 0
  const cooldownSeconds = timersKnown ? secondsRemaining(timers.resendAt, now) : 0
  const resendBlocked = timers === 'loading' || cooldownSeconds > 0

  const loadTimers = React.useCallback(async (forEmail: string) => {
    try {
      const { data } = await authApi.otpStatus(forEmail)
      const receivedAt = Date.now()
      setNow(receivedAt)
      setTimers({
        expiresAt: receivedAt + data.expires_in_seconds * 1000,
        resendAt: receivedAt + data.resend_in_seconds * 1000,
      })
    } catch {
      setTimers('unavailable')
    }
  }, [])

  React.useEffect(() => {
    if (!email) {
      navigate('/register', { replace: true })
    }
  }, [email, navigate])

  React.useEffect(() => {
    if (email) void loadTimers(email)
  }, [email, loadTimers])

  React.useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 1000)
    return () => window.clearInterval(timer)
  }, [])

  const submit = React.useCallback(
    async (value: string) => {
      if (!email || value.length !== OTP_LENGTH) return
      setError(null)
      try {
        await verifyOtp(email, value)
        navigate('/dashboard')
      } catch (err) {
        const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail
        // Backend locks the account out after OTP_MAX_ATTEMPTS wrong codes.
        // Rather than leave the user stuck re-entering codes against a locked
        // signup, send them back to /register to start over — /register safely
        // re-primes the same pending signup with a fresh code, so nothing is
        // lost except having to resubmit the form (pre-filled below).
        if (detail?.includes('Too many incorrect attempts')) {
          navigate('/register', {
            replace: true,
            state: {
              prefill: { email, username: navState?.username, full_name: navState?.full_name },
              lockedOut: true,
            },
          })
          return
        }
        setError(detail || 'Something went wrong. Please try again.')
        setCode('')
      }
    },
    [email, navState, verifyOtp, navigate],
  )

  const handleResend = async () => {
    if (!email || resendBlocked) return
    setIsResending(true)
    try {
      await resendOtp(email)
      // Re-read rather than assume a full 5:00 — the backend may not have
      // issued a new code (e.g. still inside its cooldown).
      await loadTimers(email)
      setCode('')
      setError(null)
    } catch {
      // Error toast already shown by the store.
    } finally {
      setIsResending(false)
    }
  }

  if (!email) return null

  return (
    <motion.div className="w-full" variants={formStageVariants} initial="hidden" animate="show">
      <motion.div className="mb-8" variants={formItemVariants}>
        <h1
          className="text-[#0f1011] dark:text-[#f7f8f8]"
          style={{
            fontFamily: "'Instrument Serif', 'Iowan Old Style', 'Apple Garamond', Georgia, serif",
            fontSize: '36px',
            lineHeight: 1.05,
            letterSpacing: '-0.8px',
            fontWeight: 400,
          }}
        >
          Verify your <span className="italic text-[#5e6ad2] dark:text-[#7170ff]">email</span>
        </h1>
        <p
          className="text-[#5f6166] dark:text-[#8a8f98] mt-2"
          style={{ fontSize: '15px', lineHeight: 1.5, letterSpacing: '-0.165px', fontWeight: 400 }}
        >
          Enter the 6-digit code we sent to <span style={{ fontWeight: 510 }}>{email}</span>
        </p>
      </motion.div>

      <motion.div variants={formItemVariants}>
        <motion.div variants={shakeVariants} animate={error ? 'error' : 'idle'}>
          <OtpInput
            length={OTP_LENGTH}
            value={code}
            onChange={setCode}
            onComplete={submit}
            error={!!error}
            disabled={isLoading}
          />
        </motion.div>
        {error && (
          <motion.p
            className="mt-3 text-center text-red-400"
            style={{ fontSize: '13px', letterSpacing: '-0.13px', fontWeight: 400 }}
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2, ease: easeOutExpo }}
          >
            {error}
          </motion.p>
        )}
      </motion.div>

      <motion.div className="mt-6 text-center" variants={formItemVariants}>
        {isLoading ? (
          <span
            className="inline-flex items-center gap-2 text-[#5f6166] dark:text-[#8a8f98]"
            style={{ fontSize: '13px' }}
          >
            <DotLoader /> Verifying
          </span>
        ) : (
          <p className="text-[#7a7d83] dark:text-[#62666d]" style={{ fontSize: '13px', letterSpacing: '-0.13px' }}>
            {timers === 'loading'
              ? 'Checking your code…'
              : timers === 'unavailable'
                ? "Didn't get a code? Request a new one below"
                : expirySeconds > 0
                  ? `Code expires in ${formatMMSS(expirySeconds)}`
                  : 'Your code has expired, request a new one below'}
          </p>
        )}
      </motion.div>

      <motion.div className="mt-4 text-center" variants={formItemVariants}>
        <button
          type="button"
          onClick={handleResend}
          disabled={resendBlocked || isResending}
          className="text-[#5e6ad2] hover:text-[#7170ff] dark:text-[#7170ff] dark:hover:text-[#828fff] disabled:opacity-50 disabled:pointer-events-none transition-colors"
          style={{ fontSize: '13px', fontWeight: 510, letterSpacing: '-0.13px' }}
        >
          {cooldownSeconds > 0 ? `Resend code in ${cooldownSeconds}s` : 'Resend code'}
        </button>
      </motion.div>

      <motion.div
        className="mt-6 flex items-center justify-center gap-1.5"
        style={{
          fontFamily: "'Berkeley Mono', ui-monospace, 'SF Mono', Menlo, monospace",
          fontSize: '10px',
          fontWeight: 400,
          letterSpacing: '0.14em',
          color: '#a3741f',
          textTransform: 'uppercase',
        }}
        variants={formItemVariants}
      >
        <ShieldCheck className="h-3 w-3" weight="fill" style={{ color: '#f5a524' }} />
        <span>Your data is encrypted end-to-end</span>
      </motion.div>

      <motion.button
        type="button"
        onClick={() => navigate('/login')}
        className="mt-7 w-full inline-flex items-center justify-center gap-1.5 text-[#5f6166] hover:text-[#0f1011] dark:text-[#8a8f98] dark:hover:text-[#d0d6e0] transition-colors"
        style={{ fontSize: '13px', fontWeight: 510, letterSpacing: '-0.13px' }}
        variants={formItemVariants}
      >
        <ArrowLeft className="h-3.5 w-3.5" weight="bold" />
        Back to sign in
      </motion.button>
    </motion.div>
  )
}

export default VerifyOtpPage
